import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { fmtErr } from "../lib/api";
import { LogoMark } from "../components/Logo";

const SIDE_IMG = "https://images.unsplash.com/photo-1702411200201-3061d0eea802?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBwZW50aG91c2UlMjBpbnRlcmlvciUyMG1vZGVybnxlbnwwfHx8fDE3OTE5NzQyNDN8MA&ixlib=rb-4.1.0&q=85";

export function AuthShell({ children, title, sub, testid }) {
  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2" data-testid={testid}>
      <div className="relative hidden lg:block">
        <img src={SIDE_IMG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-ink" />
        <div className="absolute bottom-12 left-12">
          <LogoMark size={40} />
          <p className="mt-6 max-w-xs font-serif text-3xl font-light leading-snug text-bone">
            Every exceptional address has a door. This is yours.
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-24">
        <div className="w-full max-w-md">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Gurgaon Estates</p>
          <h1 className="mt-3 font-serif text-4xl font-light text-bone sm:text-5xl">{title}</h1>
          <p className="mt-3 text-sm font-light text-smoke">{sub}</p>
          <div className="mt-10">{children}</div>
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(form.email, form.password);
      toast.success("Welcome back.");
      navigate(params.get("next") || "/dashboard");
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthShell title="Sign in." sub="Your desk, your pipeline, your collection — one key." testid="login-page">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <input
          type="email" required placeholder="Email" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid="login-email-input"
          className="w-full border border-white/15 bg-transparent px-4 py-3.5 text-sm font-light text-bone outline-none transition-colors placeholder:text-smoke/60 focus:border-gold"
        />
        <input
          type="password" required placeholder="Password" value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          data-testid="login-password-input"
          className="w-full border border-white/15 bg-transparent px-4 py-3.5 text-sm font-light text-bone outline-none transition-colors placeholder:text-smoke/60 focus:border-gold"
        />
        {error && <p className="text-xs text-red-400" data-testid="login-error">{error}</p>}
        <button
          type="submit" disabled={busy} data-testid="login-submit-button"
          className="flex w-full items-center justify-center gap-2 bg-gold px-4 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-ink transition-colors hover:bg-bone disabled:opacity-50"
        >
          {busy ? "Unlocking…" : "Enter"} <ArrowRight size={14} />
        </button>
      </form>

      <div className="my-6 flex items-center gap-4" aria-hidden="true">
        <span className="h-px flex-1 bg-white/10" />
        <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-smoke">or</span>
        <span className="h-px flex-1 bg-white/10" />
      </div>

      <button
        onClick={googleLogin}
        data-testid="google-login-button"
        className="flex w-full items-center justify-center gap-3 border border-white/20 px-4 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-bone transition-colors hover:border-gold hover:text-gold"
      >
        <svg width="15" height="15" viewBox="0 0 48 48" aria-hidden="true">
          <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
          <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
          <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
          <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.6-.4-3.9z"/>
        </svg>
        Continue with Google
      </button>
      <p className="mt-3 text-center text-[11px] font-light text-smoke">One tap — for buyers & tenants.</p>

      <p className="mt-8 text-xs font-light text-smoke">
        New to Gurgaon Estates? <Link to="/register" className="text-gold hover:text-bone" data-testid="login-register-link">Create an account</Link>
      </p>
    </AuthShell>
  );
}
